import React, { useState, useMemo } from 'react';
import {
  Typography, Card, Row, Col, Select, DatePicker, Button, Table, Tag,
  Statistic, Space, Spin, Empty, Divider,
} from 'antd';
import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { reportsApi, clientsApi } from '../api/endpoints';
import { STATUS_LABELS, STATUS_COLORS, PLATFORM_LABELS, PLATFORM_COLORS } from '../constants/status';
import { buildDateGroupMap } from '../styles/tokens';
import type { SnsPostStatus, Platform } from '../api/types';

const { Title, Text } = Typography;

const ReportPage: React.FC = () => {
  const [clientId, setClientId] = useState<number | undefined>();
  const [date, setDate] = useState(dayjs());

  const year = date.year();
  const month = date.month() + 1;

  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.list().then(r => ((r.data as any).data ?? r.data) as any[]),
  });

  const { data: report, isLoading } = useQuery({
    queryKey: ['report', clientId, year, month],
    queryFn: () => reportsApi.monthly({ client_id: clientId, year, month }).then(r => r.data.data),
  });

  const handleDownloadCsv = async () => {
    const response = await reportsApi.downloadCsv({ client_id: clientId, year, month });
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report_${year}${String(month).padStart(2, '0')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const columns = [
    { title: '日付', dataIndex: 'scheduled_date', key: 'date', width: 120,
      render: (v: string) => dayjs(v).format('YYYY/MM/DD') },
    { title: 'クライアント', key: 'client', render: (_: any, r: any) => r.store?.client?.name ?? '-' },
    { title: '店舗', key: 'store', render: (_: any, r: any) => r.store?.name ?? '-' },
    { title: 'PF', dataIndex: 'platform', key: 'platform', width: 120,
      render: (v: Platform) => <Tag color={PLATFORM_COLORS[v]}>{PLATFORM_LABELS[v]}</Tag> },
    { title: 'ステータス', dataIndex: 'status', key: 'status', width: 140,
      render: (v: SnsPostStatus) => <Tag color={STATUS_COLORS[v]}>{STATUS_LABELS[v]}</Tag> },
    { title: '原稿', dataIndex: 'caption', key: 'caption', ellipsis: true },
    { title: '作業数', key: 'logs', width: 80,
      render: (_: any, r: any) => r.activity_logs?.length ?? 0 },
  ];

  const dateGroupMap = useMemo(
    () => buildDateGroupMap(report?.posts ?? []),
    [report?.posts],
  );

  const summary = report?.summary;

  return (
    <div>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .ant-layout-sider, .ant-layout-header { display: none !important; }
          .ant-layout-content { margin: 0 !important; padding: 0 !important; }
          .ant-card { box-shadow: none !important; border: 1px solid #eee !important; }
        }
      `}</style>

      <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>月次レポート</Title>
        <Space>
          <Select
            placeholder="クライアント（全て）"
            allowClear
            style={{ width: 200 }}
            value={clientId}
            onChange={setClientId}
            options={(clients || []).map((c: any) => ({ value: c.id, label: c.name }))}
          />
          <DatePicker
            picker="month"
            value={date}
            onChange={(d) => d && setDate(d)}
            format="YYYY年MM月"
          />
          <Button icon={<DownloadOutlined />} onClick={handleDownloadCsv}>CSV</Button>
          <Button icon={<PrinterOutlined />} onClick={() => window.print()}>印刷/PDF</Button>
        </Space>
      </div>

      <div style={{ textAlign: 'center', marginBottom: 24 }} className="print-only">
        <Title level={3}>{year}年{month}月 作業レポート</Title>
      </div>

      {isLoading ? (
        <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
      ) : !summary || summary.total === 0 ? (
        <Card>
          <Empty description={`${year}年${month}月の作業実績はありません`} />
        </Card>
      ) : (
        <>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={6}>
              <Card><Statistic title="総投稿数" value={summary.total} /></Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic title="投稿済" value={summary.by_status?.posted ?? 0}
                  valueStyle={{ color: '#722ed1' }} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic title="準備完了" value={summary.by_status?.ready ?? 0}
                  valueStyle={{ color: '#52c41a' }} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic title="対応中" value={
                  (summary.by_status?.draft ?? 0) +
                  (summary.by_status?.writing ?? 0) +
                  (summary.by_status?.photo_requested ?? 0) +
                  (summary.by_status?.photo_received ?? 0)
                } />
              </Card>
            </Col>
          </Row>

          {summary.by_store && Object.keys(summary.by_store).length > 0 && (
            <Card title="店舗別投稿数" style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                {Object.entries(summary.by_store).map(([name, count]) => (
                  <Col key={name} xs={12} sm={8} md={6}>
                    <Statistic title={name} value={count as number} />
                  </Col>
                ))}
              </Row>
            </Card>
          )}

          <Card title="投稿一覧">
            <Table
              dataSource={report?.posts}
              columns={columns}
              rowKey="id"
              pagination={false}
              size="small"
              rowClassName={(record: any) =>
                dateGroupMap[record.scheduled_date] ?? ''
              }
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default ReportPage;
